import LinkModal from '@UI/modals/LinkModal';
import ModalErrorBoundary from '@UI/modals/ModalErrorBoundary';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import { getAngularService } from '@kathondvla/react-shared-components/src/helpers/angularReactHelper';
import type { EditAsideLinks } from '@nodeTypeConfig/configTypes';
import {
  addLinkReferenceNodeAction,
  editLinkReferenceNodeAction,
  removeNodeAction,
} from '@store/actions/documentActions';
import { getResourceKey } from '@store/helpers/documentHelpers';
import { any } from 'prop-types';
import type React from 'react';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useEditHref } from '../../../hooks/UseEditHref';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import './asideLinks.scss';
import { selectAsideLinks } from './asideLinksSelectors';

export type Link = {
  title: string;
  url: string;
};

const AsideLinks: React.FC<{
  config: EditAsideLinks;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const editHref = useEditHref();
  const nodeKey = getResourceKey(editHref);

  const dispatch = useDispatch();
  const confirmationService = getAngularService('ConfirmationService');
  const links = useTypedSelector((state) => selectAsideLinks(state, editHref));

  const linkModalRef = useRef<Modal>();

  const addLink = async () => {
    linkModalRef.current?.open({
      modalTitle: config.options?.modalTitle,
      onSubmit: async (link: Link) => {
        if (link) {
          dispatch(
            addLinkReferenceNodeAction(
              nodeKey,
              link.title,
              link.url,
              config.options?.type === 'group',
            ),
          );
        }
      },
    });
  };

  const editLink = async (item) => {
    linkModalRef.current?.open({
      link: { title: item.title, url: item.href },
      modalTitle: config.options?.modalTitle || 'Link bewerken',
      onSubmit: async (link: Link) => {
        if (link) {
          dispatch(editLinkReferenceNodeAction(nodeKey, item.key, link.title, link.url));
        }
      },
    });
  };

  const deleteLink = async (link) => {
    const confirmed = await confirmationService.confirmDeletion();
    if (confirmed) {
      dispatch(removeNodeAction(link.key, true, nodeKey));
    }
  };

  return (
    <>
      <div className="panel panel-default">
        <div className="panel-heading">
          <div className="panel-controls panel-controls-left">
            <h3 className="panel-title">{config.label}</h3>
          </div>
          <div className="panel-controls panel-controls-right">
            <div className="btn-toolbar">
              {!readonly && (
                <button className="btn btn-default" onClick={addLink}>
                  <span className="glyphicon glyphicon-add" />
                  <span>Voeg toe</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {links.length !== 0 && (
          <div className="structure">
            {links.map((link) => (
              <div className="row table-row" key={link.key}>
                <>
                  <div
                    className={`col-md-12 link-row ${
                      link.isDeletedByProposal ? 'deleted-hidden-child' : ''
                    }`}
                  >
                    <div>
                      <a href={link.href} target="_blank" rel="noreferrer">
                        {link.href}
                      </a>
                      <br />
                      {link.title}
                    </div>
                  </div>
                  {!readonly && !link.isDeletedByProposal && (
                    <div
                      className="btn-group btn-group-xs row-actions"
                      role="group"
                      aria-label="..."
                    >
                      <button
                        type="button"
                        className="btn btn-default"
                        onClick={() => editLink(link)}
                      >
                        <span className="glyphicon glyphicon-edit" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-default"
                        onClick={() => deleteLink(link)}
                      >
                        <span className="glyphicon glyphicon-delete" />
                      </button>
                    </div>
                  )}
                </>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        ref={linkModalRef}
        size="sm"
        component={<LinkModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </>
  );
};

AsideLinks.displayName = 'AsideLinks';

export default AsideLinks;

AsideLinks.propTypes = {
  config: any,
  readonly: any,
};
